import React, { useState, useEffect } from 'react';
import QueueManager from './components/QueueManager';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Login from './components/Login';
import UserPage from './components/UserPage';
import QrCodePage from './components/QrCodePage';
import CustomerTrackingPage from './components/CustomerTrackingPage';
import AllEnteredPage from './components/AllEnteredPage';
import OrdersPage from './components/Orders';
import TableSettingsPage from './components/TableSettingsPage';
import ReservationPage from './components/ReservationPage';
import BusinessSettings from './components/BusinessSettings';
import QueueService from './components/QueueService';
import './App.css';
import ChatPage from './components/ChatPage';
import ChatWindow from './components/ChatWindow';
import { getToken } from "firebase/messaging";
import { messaging } from "./firebase/firebaseConfig";
import RestrictReservations from './components/RestrictReservations';
import BusinessAnalyticsPage from './components/BusinessAnalyticsPage';
import CampaignsPage from './components/CampaignsPage';
import UsersPage from './components/UsersPage';
import FlowBuilderPage from './components/FlowBuilder/FlowBuilderPage';
import TemplatesPage from './components/TemplatesPage';
import BrandSettingsPage from './components/BrandSettingsPage';
import DineInPage from './components/DineInPage';
import OrderSettingsPage from './components/OrderSettingsPage';
import createApp from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';
import OrdersCreatePage from './components/OrdersCreatePage';
import WashroomFeedback from './components/WashroomFeedback';
import ReviewsPage from './components/ReviewsPage';
import GbpMappingPage from './components/GbpMappingPage';
import DeliveryOrderPage from './components/DeliveryOrderPage';



import safeLocalStorage from './utils/safeStorage';

const apiUrl = process.env.REACT_APP_API_URL;
const BASE_URL = `${apiUrl}/business`;
const { VITE_APP_VAPID_KEY } = "BC4SBQ1Uo9aB4CMPu93svGk4JLZTcsifaPUVL6ujDMVpLg6v80uOshaMfUXp1FeqSfKGp4_bHnJ8RYaKOu7pSoY";

// Monkey-patch fetch to globally inject Bearer token for Shopify / general API calls
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  let [resource, config] = args;

  let token = safeLocalStorage.getItem('token');

  // If we are in Shopify, always ask App Bridge for a fresh token!
  try {
    if (window.shopify && window.shopify.idToken) {
      token = await window.shopify.idToken();
      safeLocalStorage.setItem('token', token);
    } else if (window.shopifyAppInstance) {
      token = await getSessionToken(window.shopifyAppInstance);
      safeLocalStorage.setItem('token', token);
    }
  } catch (err) {
    console.error("Failed to fetch fresh Shopify token:", err);
  }
  if (token && token.startsWith('eyJ')) {
    if (!config) config = {};
    if (config.headers instanceof Headers) {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`
      };
    }
    args[1] = config;
  }

  return originalFetch.apply(this, args);
};

function ShopifyAuthenticator({ host, onShopifyAuth, onShopifyError }) {
  useEffect(() => {
    console.log("Raw Host param used:", host);

    let retryCount = 0;
    const maxRetries = 5;

    // Top-level initialization
    const apiKey = '99ab2bb4fb642e25ee0915a267107631';
    let app = null;
    if (apiKey && host && !(window.shopify && window.shopify.idToken)) {
      app = createApp({ apiKey, host, forceRedirect: true });
      window.shopifyAppInstance = app; // Expose globally for the fetch interceptor
    }

    const fetchToken = async () => {
      try {
        let tokenPromise = null;
        if (window.shopify && window.shopify.idToken) {
          // Modern App Bridge via CDN script
          tokenPromise = window.shopify.idToken();
        } else if (app) {
          // Fallback to createApp
          tokenPromise = getSessionToken(app);
        } else {
          throw new Error("App Bridge not initialized");
        }

        // Add 3-second Race Timer
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Token fetch timed out after 3 seconds")), 3000)
        );

        const token = await Promise.race([tokenPromise, timeoutPromise]);

        if (token) {
          onShopifyAuth(token);
        } else {
          throw new Error("Token was null or undefined");
        }
      } catch (err) {
        console.error(`Shopify Token Fetch Error (Attempt ${retryCount + 1}):`, err);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(fetchToken, 500); // Retry after 500ms
        } else {
          if (onShopifyError) {
            onShopifyError(err.message || 'Failed to authenticate with Shopify after multiple attempts');
          }
        }
      }
    };

    fetchToken();
  }, [host, onShopifyAuth, onShopifyError]);

  return null;
}

function AppRoutes({ authenticated, businessId, businessUserId, businessIds, token, handleLogout, isOffline, handleLogin, handleBusinessSwitch }) {
  const location = useLocation();

  // Device registration function (moved from App)
  async function registerDevice() {
    if (!messaging) {
      console.warn('Firebase Messaging not available or supported in this browser.');
      return null;
    }
    try {
      const fcm_token = await getToken(messaging, {
        vapidKey: VITE_APP_VAPID_KEY,
      });
      const response = await fetch(`${BASE_URL}/register_device?auth_token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          entity_id: businessId,
          entity_type: 'business',
          fcm_token: fcm_token,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to register device:', error);
      return null;
    }
  }

  // Device registration and notification permission handling
  function NotificationManager({ businessId, token }) {
    useEffect(() => {
      if (businessId && token && location.pathname !== '/user_form') {
        registerDevice().then(data => {
          if (data) {
            console.log('Device registered successfully:', data);
          }
        });
        requestPermission();
      }
    }, [businessId, token, location.pathname]);
    async function requestPermission() {
      try {
        if (typeof Notification !== 'undefined' && Notification.requestPermission) {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            console.log('Permission granted');
          } else if (permission === "denied") {
            console.log("You denied the notification");
          }
        }
      } catch (err) {
        console.warn('Notification permission error:', err);
      }
    }
    return null;
  }
  return (
    <div className="App">
      {authenticated && <NotificationManager businessId={businessId} token={token} />}
      {isOffline && (
        <div className="offline-banner">
          <p>You are currently offline. Some features may not be available.</p>
        </div>
      )}
      <Routes>
        <Route
          path="/"
          element={
            !authenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <QueueManager
                business_id={businessId}
                business_user_id={businessUserId}
                token={token}
                handleLogout={handleLogout}
                businessIds={businessIds}
                handleBusinessSwitch={handleBusinessSwitch}
              />
            )
          }
        />
        <Route path="/user_form" element={<UserPage token={token} />} />
        <Route path="/make_reservation" element={<ReservationPage />} />
        <Route path="/qr-code" element={<QrCodePage token={token} business_id={businessId} />} />
        <Route path="/whatsapp" element={<ChatPage token={token} />} />
        <Route path="/restrict-reservations" element={<RestrictReservations token={token} business_id={businessId} />} />
        <Route path="/table-settings" element={<TableSettingsPage token={token} business_id={businessId} />} />
        <Route path="/business-analytics" element={<BusinessAnalyticsPage token={token} />} />
        <Route path="/campaigns" element={<CampaignsPage token={token} />} />
        <Route path="/templates" element={<TemplatesPage token={token} />} />
        <Route path="/users" element={<UsersPage token={token} business_id={businessId} />} />
        <Route
          path="/orders"
          element={
            !authenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <OrdersPage token={token} />
            )
          }
        />
        <Route
          path="/orders/create"
          element={
            !authenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <OrdersCreatePage token={token} />
            )
          }
        />
        <Route path="/chat/:phone" element={<ChatWindow />} />
        <Route path="/customer_tracking/:final_str" element={<CustomerTrackingPage />} />
        <Route path="/all-entered" element={<AllEnteredPage token={token} business_id={businessId} />} />
        <Route path="/business-settings" element={<BusinessSettings token={token} business_id={businessId} />} />
        <Route path="/flow-builder" element={<FlowBuilderPage token={token} business_id={businessId} />} />
        <Route path="/brand-settings" element={<BrandSettingsPage token={token} business_id={businessId} />} />
        <Route path="/dinein/:business_id/:mapping_id" element={<DineInPage />} />
        <Route path="/order-settings" element={<OrderSettingsPage token={token} business_id={businessId} />} />
        <Route path="/washroom-feedback" element={<WashroomFeedback />} />
        <Route
          path="/reviews"
          element={
            !authenticated ? (
              <Login onLogin={handleLogin} />
            ) : (
              <ReviewsPage token={token} business_id={businessId} />
            )
          }
        />
        <Route path="/gbp-mapping" element={<GbpMappingPage />} />
        <Route path="/brand/:brand_id/:customer_id/order" element={<DeliveryOrderPage />} />
        <Route path="/brand/:brand_id/:customer_id/delivery" element={<DeliveryOrderPage />} />
        <Route path="/:business_id/:customer_id/delivery" element={<DeliveryOrderPage />} />
        <Route path="/:business_id/:customer_id/order" element={<DeliveryOrderPage />} />

      </Routes>
    </div>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [businessUserId, setBusinessUserId] = useState(null);
  const [businessIds, setBusinessIds] = useState([]);
  const [token, setToken] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [loading, setLoading] = useState(true);
  const [shopifyError, setShopifyError] = useState(null);

  useEffect(() => {
    const storedAuth = safeLocalStorage.getItem('authenticated') === 'true';
    const storedBusinessId = safeLocalStorage.getItem('businessId');
    const storedBusinessUserId = safeLocalStorage.getItem('businessUserId');
    const storedToken = safeLocalStorage.getItem('token');
    const storedBusinessIds = safeLocalStorage.getItem('businessIds');

    if (storedAuth && storedBusinessId && storedBusinessUserId && storedToken) {
      setAuthenticated(true);
      setBusinessId(parseInt(storedBusinessId, 10));
      setBusinessUserId(parseInt(storedBusinessUserId, 10));
      setToken(storedToken);
      if (storedBusinessIds && storedBusinessIds !== "undefined") {
        try {
          const parsed = JSON.parse(storedBusinessIds);
          setBusinessIds(parsed);

          // If they are just IDs (backward compatibility), fetch their names
          if (parsed.length > 0 && typeof parsed[0] !== 'object') {
            fetchBusinessNames(parsed).then(enriched => {
              setBusinessIds(enriched);
              safeLocalStorage.setItem('businessIds', JSON.stringify(enriched));
            });
          }
        } catch (e) {
          console.error("Failed to parse businessIds", e);
          setBusinessIds([]);
        }
      }
    }
    setLoading(false);
  }, []);

  // Service Worker registration for offline support
  useEffect(() => {
    try {
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          try {
            navigator.serviceWorker.register('/service-worker.js')
              .then(registration => {
                console.log('Service Worker registered with scope:', registration.scope);
              })
              .catch(error => {
                console.error('Service Worker registration failed:', error);
              });
          } catch (swErr) {
            console.warn('Service Worker registration error:', swErr);
          }
        });
      }
    } catch (err) {
      console.warn('Service Worker check error:', err);
    }
  }, []);

  // Optional: Listen for online/offline events to update UI or show notifications
  useEffect(() => {
    const handleOffline = () => {
      console.log('You are offline.');
      setIsOffline(true);
    };

    const handleOnline = () => {
      console.log('You are online.');
      setIsOffline(false);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const fetchBusinessNames = async (ids) => {
    if (!ids || ids.length === 0) return [];
    if (typeof ids[0] === 'object') return ids;

    try {
      const enriched = await Promise.all(ids.map(async (id) => {
        try {
          const info = await QueueService.GetBusinessInfo(id);
          return { id, name: info.business_name };
        } catch (e) {
          return { id, name: `Business ${id}` };
        }
      }));
      return enriched;
    } catch (e) {
      return ids.map(id => ({ id, name: `Business ${id}` }));
    }
  };

  const handleLogin = async (business_id, business_user_id, token, business_ids = []) => {
    setBusinessId(business_id);
    setBusinessUserId(business_user_id);
    setToken(token);
    setAuthenticated(true);

    safeLocalStorage.setItem('authenticated', 'true');
    safeLocalStorage.setItem('businessId', business_id.toString());
    safeLocalStorage.setItem('businessUserId', business_user_id.toString());
    safeLocalStorage.setItem('token', token);

    // Fetch names for all businesses to show in switcher
    const businessIdsWithNames = await fetchBusinessNames(business_ids);
    setBusinessIds(businessIdsWithNames);
    safeLocalStorage.setItem('businessIds', JSON.stringify(businessIdsWithNames));

    // Check if there's a stored redirect path
    const redirectPath = safeLocalStorage.getItem('redirectAfterLogin');
    if (redirectPath && redirectPath !== '/') {
      safeLocalStorage.removeItem('redirectAfterLogin');
      window.location.href = redirectPath;
    }
  };

  const handleBusinessSwitch = (newBusinessId) => {
    setBusinessId(newBusinessId);
    safeLocalStorage.setItem('businessId', newBusinessId.toString());
    // Potentially reload to clear any stale state
    // window.location.reload(); 
  };

  const handleLogout = () => {
    const currentToken = token || safeLocalStorage.getItem('token');
    if (currentToken) {
      QueueService.Logout(currentToken);
    }

    setAuthenticated(false);
    setBusinessId(null);
    setBusinessUserId(null);
    setToken(null);
    setBusinessIds([]);
    safeLocalStorage.removeItem('authenticated');
    safeLocalStorage.removeItem('businessId');
    safeLocalStorage.removeItem('businessUserId');
    safeLocalStorage.removeItem('token');
    safeLocalStorage.removeItem('businessIds');
    safeLocalStorage.removeItem('queueStarted');
  };

  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host');
  const shop = urlParams.get('shop');
  const isShopify = !!(host || shop);

  useEffect(() => {
    if (window.self !== window.top) {
      document.body.classList.add('is-embedded');
    }
  }, []);

  const handleShopifyAuth = (newToken) => {
    if (token !== newToken) {
      const urlBusId = urlParams.get('business_id');
      if (urlBusId) {
        setBusinessId(parseInt(urlBusId, 10));
        safeLocalStorage.setItem('businessId', urlBusId);
      }
      setToken(newToken);
      setAuthenticated(true);
      safeLocalStorage.setItem('token', newToken);
      safeLocalStorage.setItem('authenticated', 'true');
    }
  };

  const appContent = (
    <Router>
      <AppRoutes
        authenticated={authenticated}
        businessId={businessId}
        businessUserId={businessUserId}
        businessIds={businessIds}
        token={token}
        handleLogout={handleLogout}
        isOffline={isOffline}
        handleLogin={handleLogin}
        handleBusinessSwitch={handleBusinessSwitch}
      />
    </Router>
  );

  if (loading) return <div>Loading...</div>;

  if (isShopify && host) {
    if (shopifyError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#d72c0d' }}>Authentication Failed</h2>
          <p style={{ color: '#6d7175' }}>{shopifyError}</p>
          <p style={{ color: '#6d7175', fontSize: '14px' }}>Please try refreshing the page or contact support.</p>
        </div>
      );
    }

    return (
      <>
        <ShopifyAuthenticator host={host} onShopifyAuth={handleShopifyAuth} onShopifyError={setShopifyError} />
        {appContent}
      </>
    );
  }

  return appContent;
}

export default App;
